import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Activity, Bell, DoorOpen, Smartphone, AlertTriangle, TrendingUp, Users, BarChart3, Shield, Clock, Heart, CheckCircle } from "lucide-react";

export default function Services() {
  const { t } = useTranslation();

  const systemComponents = [
    { icon: Activity, title: t("services.comp1Title"), description: t("services.comp1Desc") },
    { icon: Bell, title: t("services.comp2Title"), description: t("services.comp2Desc") },
    { icon: AlertTriangle, title: t("services.comp3Title"), description: t("services.comp3Desc") },
    { icon: DoorOpen, title: t("services.comp4Title"), description: t("services.comp4Desc") },
    { icon: Smartphone, title: t("services.comp5Title"), description: t("services.comp5Desc") }
  ];

  const benefits = [
    { icon: Clock, title: t("services.ben1Title"), description: t("services.ben1Desc") },
    { icon: TrendingUp, title: t("services.ben2Title"), description: t("services.ben2Desc") },
    { icon: Users, title: t("services.ben3Title"), description: t("services.ben3Desc") },
    { icon: BarChart3, title: t("services.ben4Title"), description: t("services.ben4Desc") },
    { icon: Shield, title: t("services.ben5Title"), description: t("services.ben5Desc") },
    { icon: Heart, title: t("services.ben6Title"), description: t("services.ben6Desc") }
  ];

  const scrollToContact = () => {
    const element = document.querySelector("#kontakti");
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <section id="pakalpojumi" className="section-spacing bg-white" data-testid="services-section">
      <div className="section-container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">{t("services.badge")}</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("services.title")}</h2>
          <p className="text-lg md:text-xl text-muted-foreground">{t("services.description")}</p>
        </div>

        <div className="mb-20">
          <h3 className="text-2xl font-bold text-foreground mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("services.systemTitle")}</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {systemComponents.map((component, index) => (
              <div key={index} className={`p-6 rounded-2xl border hover:shadow-lg transition-shadow duration-300 ${index === 0 ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`} data-testid={`system-component-${index + 1}`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${index === 0 ? 'bg-white/20' : 'bg-primary/10'}`}>
                  <component.icon className={`w-7 h-7 ${index === 0 ? 'text-white' : 'text-primary'}`} />
                </div>
                <h4 className={`text-lg font-bold mb-2 ${index === 0 ? 'text-white' : 'text-foreground'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{component.title}</h4>
                <p className={index === 0 ? 'text-white/90' : 'text-muted-foreground'}>{component.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-secondary rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("services.benefitsTitle")}</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t("services.benefitsDesc")}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300" data-testid={`benefit-${index + 1}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{benefit.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-primary rounded-2xl p-8 text-center text-white">
            <div className="flex items-center justify-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8" />
              <h4 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("services.highlightTitle")}</h4>
            </div>
            <p className="text-white/90 max-w-2xl mx-auto mb-6">{t("services.highlightDesc")}</p>
            <Button onClick={scrollToContact} className="bg-white text-primary hover:bg-white/90 h-14 px-8 rounded-full font-semibold text-lg" data-testid="services-cta">{t("services.highlightCta")}</Button>
          </div>
        </div>
      </div>
    </section>
  );
}
